import React, { useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Pencil, Trash3 } from 'react-bootstrap-icons'
import { TimelineContext } from '../../../../../../contexts/TimelineContext'
import { CampaignContext } from '../../../../../../contexts/CampaignContext'
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext'
import { toastError, toastSuccess } from '../../../../../FormComponents/Toasts'
import axiosService from '../../../../../../utils/AxiosService'
import {
  formatTimelineDateForInput,
  isDateOutsideCampaignPeriod,
  isTimelineEndDatePassed,
} from '../../../../../../utils/timelineAssetDates'
import {
  findTimelinePlacement,
  resolveTimelineAssetNote,
} from '../../../../../../utils/timelineAssetPlacement'
import { isCustomProjectAsset, projectAssetPhaseId } from '../../../../../../utils/projectAssetPhases'

export function usePmAssetListItem({ asset, onDelete, isUsedInTimeline }) {
  const { id: campaignId } = useParams()
  const { timelineState, setJumpToDate } = useContext(TimelineContext)
  const { campaign, campaignScenarios, projectAssets } = useContext(CampaignContext)
  const readOnly = useReadOnly()

  const isProjectAsset = Boolean(onDelete)
  const isCustomAsset = isProjectAsset && isCustomProjectAsset(asset)
  const canDelete = isCustomAsset
  const showSidebarDates = timelineState.sidebarShowAssetDates
  const campaignScenario = campaignScenarios[0]

  const saveTimeoutRef = useRef(null)
  const [isSaving, setIsSaving] = useState(false)
  const [hasDateError, setHasDateError] = useState(false)
  const [lastSavedSignature, setLastSavedSignature] = useState('')
  const [dates, setDates] = useState({ startDate: '', endDate: '' })
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [note, setNote] = useState('')

  const timelinePlacement = isUsedInTimeline
    ? findTimelinePlacement(timelineState.timelineItems, asset, {
        matchByProjectAssetId: isProjectAsset,
      })
    : null

  const campaignStartDate = formatTimelineDateForInput(campaign?.start_date)
  const campaignEndDate = formatTimelineDateForInput(campaign?.end_date)
  const currentStartDate = formatTimelineDateForInput(
    timelinePlacement?.start_date || asset?.start_date || asset?.start,
  )
  const currentEndDate = formatTimelineDateForInput(
    timelinePlacement?.end_date || asset?.end_date || asset?.end,
  )
  const serverNote = resolveTimelineAssetNote(asset, timelinePlacement)
  const isPassed = isTimelineEndDatePassed(currentEndDate)

  useEffect(() => {
    setDates({ startDate: currentStartDate, endDate: currentEndDate })
    setNote(serverNote)
    setLastSavedSignature(`${currentStartDate}|${currentEndDate}|${serverNote}`)
    setHasDateError(false)
  }, [currentStartDate, currentEndDate, serverNote])

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  const persistName = async (nextName) => {
    if (!isCustomAsset || !nextName) return

    const trimmed = nextName.trim()
    if (!trimmed || trimmed === asset.name) return

    const updated = await axiosService.patch(
      `/api/project_manager/campaigns/${campaignId}/project_assets/${asset.id}`,
      { project_asset: { name: trimmed } },
    )
    asset.name = updated.name
  }

  const persistPhase = async (nextPhase) => {
    if (!isProjectAsset || !nextPhase) return

    const currentPhase = asset.phase || projectAssetPhaseId(asset)
    if (nextPhase === currentPhase) return

    const updated = await axiosService.patch(
      `/api/project_manager/campaigns/${campaignId}/project_assets/${asset.id}`,
      { project_asset: { phase: nextPhase } },
    )
    asset.phase = updated.phase
  }

  const removeTimelinePlacement = async (placement) => {
    if (!placement || !campaignScenario) return

    const placementDate = placement.start_date
    const newItems = new Map(timelineState.timelineItems)
    const dateItems = newItems.get(placementDate) || []
    const remaining = dateItems.filter((item) => String(item.id) !== String(placement.id))

    if (remaining.length > 0) {
      newItems.set(placementDate, remaining)
    } else {
      newItems.delete(placementDate)
    }
    timelineState.timelineItems = newItems

    if (isProjectAsset) {
      campaignScenario.campaign_scenarios_project_assets =
        campaignScenario.campaign_scenarios_project_assets.filter((a) => +a.id !== +placement.id)

      await axiosService.delete(
        `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${campaignScenario.id}/campaign_scenarios_project_assets/${placement.id}`,
      )
    } else {
      campaignScenario.actions = campaignScenario.actions.map((a) =>
        +a.id === +placement.id ? { ...a, start_date: null, end_date: null } : a,
      )

      await axiosService.patch(
        `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${campaignScenario.id}/actions/${placement.id}`,
        { campaign_scenario_action: { start_date: null, end_date: null } },
      )
    }
  }

  const clearAssetSchedule = async (nextNotes = undefined) => {
    const effectiveNotes = nextNotes !== undefined ? nextNotes : note
    if (!campaignScenario) return

    setIsSaving(true)

    try {
      if (isProjectAsset) {
        const updated = await axiosService.patch(
          `/api/project_manager/campaigns/${campaignId}/project_assets/${asset.id}`,
          { project_asset: { start: null, end: null, notes: effectiveNotes } },
        )
        asset.start = updated.start
        asset.end = updated.end
        asset.notes = updated.notes

        if (timelinePlacement) {
          await removeTimelinePlacement(timelinePlacement)
        }
      } else if (timelinePlacement) {
        await axiosService.patch(
          `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${campaignScenario.id}/actions/${asset.id}`,
          {
            campaign_scenario_action: {
              start_date: null,
              end_date: null,
              notes: effectiveNotes,
            },
          },
        )
        asset.start_date = null
        asset.end_date = null
        asset.notes = effectiveNotes
        await removeTimelinePlacement(timelinePlacement)
      } else {
        await axiosService.patch(
          `/api/project_manager/campaigns/${campaignId}/campaign_scenarios/${campaignScenario.id}/actions/${asset.id}`,
          {
            campaign_scenario_action: {
              start_date: null,
              end_date: null,
              notes: effectiveNotes,
            },
          },
        )
        asset.start_date = null
        asset.end_date = null
        asset.notes = effectiveNotes
      }

      setLastSavedSignature(`||${effectiveNotes}`)
      toastSuccess('Asset updated')
    } catch (error) {
      toastError('Failed to update asset:', error)
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const persistDateRange = async (nextStartDate, nextEndDate, nextNotes = undefined) => {
    const effectiveNotes = nextNotes !== undefined ? nextNotes : note
    const nextSignature = `${nextStartDate}|${nextEndDate}|${effectiveNotes}`
    if (nextSignature === lastSavedSignature) return
    if (!nextStartDate || !nextEndDate || !campaignScenario) return

    if (nextEndDate < nextStartDate) {
      setHasDateError(true)
      return
    }

    if (
      isDateOutsideCampaignPeriod(nextStartDate, campaignStartDate, campaignEndDate) ||
      isDateOutsideCampaignPeriod(nextEndDate, campaignStartDate, campaignEndDate)
    ) {
      toastError('Asset dates must be within the campaign period.')
      return
    }

    setHasDateError(false)
    setIsSaving(true)

    try {
      if (isProjectAsset && !isUsedInTimeline) {
        await timelineState.addProjectAssetToTimeline(
          projectAssets,
          campaignScenario,
          asset.id,
          nextStartDate,
          nextEndDate,
          effectiveNotes,
        )
      } else if (!isProjectAsset && !isUsedInTimeline) {
        await timelineState.addActionToTimeline(
          campaignScenario,
          asset.id,
          nextStartDate,
          nextEndDate,
          effectiveNotes,
        )
      } else {
        const editableAsset = timelinePlacement || asset
        const oldDate = editableAsset?.start_date || nextStartDate
        await timelineState.updateTimelineItemDate(
          editableAsset,
          oldDate,
          nextStartDate,
          nextEndDate,
          campaignScenario,
          effectiveNotes,
        )
      }

      setLastSavedSignature(nextSignature)
      if (nextNotes !== undefined) toastSuccess('Asset updated')
    } catch (error) {
      toastError('Failed to update asset:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const scheduleAutoSave = (nextDates) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => {
      persistDateRange(nextDates.startDate, nextDates.endDate)
    }, 450)
  }

  const handleDateChange = (field, value) => {
    setDates((prev) => {
      let nextDates = { ...prev, [field]: value }

      if (value && isDateOutsideCampaignPeriod(value, campaignStartDate, campaignEndDate)) {
        toastError(
          `Date must be within campaign period. (start date: ${campaignStartDate}, end date: ${campaignEndDate})`,
          { autoClose: 5000 },
        )
        nextDates[field] = ''
      }

      if (field === 'startDate' && !nextDates.endDate && nextDates.startDate) {
        nextDates.endDate = nextDates.startDate
      }

      const invalidRange =
        nextDates.startDate && nextDates.endDate && nextDates.endDate < nextDates.startDate

      if (invalidRange) {
        toastError('End date must be on or after start date.')
        nextDates[field] = ''
      }

      setHasDateError(!!invalidRange)
      scheduleAutoSave(nextDates)
      return nextDates
    })
  }

  const handleRowClick = () => {
    const target = timelinePlacement || asset
    if (!target?.start_date) return
    setJumpToDate(target)
  }

  const handleEditSave = async ({ startDate, endDate, note: nextNote, phase: nextPhase, name: nextName }) => {
    setNote(nextNote)
    setDates({ startDate: startDate || '', endDate: endDate || '' })

    const hasBothDates = Boolean(startDate && endDate)
    const hasNoDates = !startDate && !endDate

    if (hasBothDates) {
      await persistDateRange(startDate, endDate, nextNote)
    } else if (hasNoDates) {
      await clearAssetSchedule(nextNote)
    }

    await persistPhase(nextPhase)
    await persistName(nextName)
  }

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true)
      await onDelete()
      setDeleteModalOpen(false)
    } catch (error) {
      toastError(error)
    } finally {
      setIsDeleting(false)
    }
  }

  const kebabItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="text-rb-green" />,
      onClick: () => setEditModalOpen(true),
    },
  ]

  if (canDelete) {
    kebabItems.push({
      key: 'delete',
      label: 'Delete',
      icon: <Trash3 className="text-red-500" />,
      disabled: isUsedInTimeline,
      title: isUsedInTimeline ? 'Remove this asset from the timeline before deleting' : undefined,
      onClick: () => setDeleteModalOpen(true),
    })
  }

  return {
    readOnly,
    asset,
    isProjectAsset,
    isCustomAsset,
    canDelete,
    showSidebarDates,
    isUsedInTimeline,
    note,
    dates,
    hasDateError,
    isSaving,
    editModalOpen,
    deleteModalOpen,
    isDeleting,
    campaignStartDate,
    campaignEndDate,
    currentStartDate,
    currentEndDate,
    kebabItems: readOnly ? [] : kebabItems,
    cardClassName: `mb-2 w-full min-w-0 cursor-pointer rounded-md border-1 px-2 py-1 ${
      isPassed
        ? 'border-gray-300 bg-gray-100 opacity-75 shadow-none grayscale dark:border-gray-600 dark:bg-rb-dark-mode-lighter/60'
        : 'border-primary bg-white shadow-sm dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode-lighter'
    }`,
    titleVariant: isProjectAsset ? 'project' : 'scenario',
    isPassed,
    showAuthUsername: !isProjectAsset,
    editModalProps: {
      initialPhase: isProjectAsset ? projectAssetPhaseId(asset) : undefined,
      showPhaseSelect: isProjectAsset,
      showNameField: isCustomAsset,
    },
    setEditModalOpen,
    setDeleteModalOpen,
    handleRowClick,
    handleDateChange,
    handleEditSave,
    handleDeleteConfirm,
  }
}
